/**
 * Printing a receipt, and the four ways it can fail into something that works.
 *
 * The rule from `CLAUDE.md`: **every native capability degrades to what already
 * worked.** No shell, no printer configured, a printer that is off, a queue that
 * refused, a webview with no canvas — all of it ends at `window.print()`, and
 * the caller is told which.
 *
 * Note what is *not* checked: `inShell()`. A kiosk inside the shell with no
 * printer configured must take the browser path, and a check on the shell
 * rather than on the capability sends it to a printer that is not there.
 */
import type { DecodedImage, ReceiptTemplate, Slip, SlipBranch, SlipOrder } from '@mgc/receipt'
import {
  BAND_ROWS_FLOW_CONTROLLED,
  buildSlip,
  drawSlip,
  printViaBrowser,
  renderHtml,
  renderText,
  toEscPosRaster
} from '@mgc/receipt'
import { templateInHand } from '@/services/receipt-template'
import { printRaw, shellInfo, shellLog } from './native'
import { RECEIPT_FONT_CSS, receiptFontReady } from './receipt-font'

export type PrintOutcome =
  | { readonly via: 'thermal'; readonly mode: 'raster' | 'text' }
  | { readonly via: 'browser'; readonly because: PrintFallback }

export type PrintFallback = 'no-shell' | 'no-printer' | 'no-canvas' | 'refused'

/**
 * Logos, decoded once.
 *
 * `drawSlip` is synchronous and decoding is not, so it happens here — and the
 * result is kept, because the same logo is on every receipt of the day and
 * decoding a PNG per sale is work done in front of a customer. Keyed by the data
 * URL, so a logo that changes is a different key and the old one falls out with
 * the map when the page reloads.
 */
const decoded = new Map<string, DecodedImage>()

async function decodeLogos(slip: Slip): Promise<ReadonlyMap<string, DecodedImage>> {
  for (const block of slip.blocks) {
    if (block.kind !== 'header' || block.image === null || decoded.has(block.image.data)) continue
    try {
      const image = new Image()
      image.src = block.image.data
      await image.decode()
      decoded.set(block.image.data, image)
    } catch {
      // A logo that will not decode is a gap on the slip, not a failed receipt:
      // `raster.ts` reserves its height either way, so nothing below it moves.
      shellLog('warn', 'the receipt logo could not be decoded; printing without it')
    }
  }
  return decoded
}

/** The browser path's page, with the receipt's face carried inside it. */
const html = (slip: Slip) => renderHtml(slip, { fontCss: RECEIPT_FONT_CSS })

export async function printSlip(slip: Slip): Promise<PrintOutcome> {
  const info = await shellInfo()

  if (info === undefined) {
    await printViaBrowser(html(slip))
    return { via: 'browser', because: 'no-shell' }
  }

  if (!info.printer) {
    await printViaBrowser(html(slip))
    return { via: 'browser', because: 'no-printer' }
  }

  try {
    await printRaw(await slipBytes(slip))
    return { via: 'thermal', mode: info.receipt.mode }
  } catch (error) {
    // A printer that is off, a queue that refused, a canvas that would not
    // allocate. The banner says the thermal print failed; the customer still
    // gets a receipt.
    shellLog('warn', `thermal print failed, falling back to the browser: ${String(error)}`)
    await printViaBrowser(html(slip))
    return { via: 'browser', because: error instanceof NoCanvas ? 'no-canvas' : 'refused' }
  }
}

/**
 * What a thermal printer would be sent for this slip, rendered and not printed.
 *
 * Split out of `printSlip` for the one caller that has a printer but is not the
 * one driving it: the shell's setup window asks for a test print, and the slip
 * has to be drawn here — `packages/receipt` runs in the page and the shell only
 * carries bytes — before Rust sends it. See `shell_test_print`.
 *
 * Throws rather than falling back, which is the difference from `printSlip`.
 * Its caller is not a customer waiting for a receipt; it is an integrator
 * standing at a printer, and `window.print()` on the selling page is not an
 * answer to a question somebody asked in another window.
 */
export async function slipBytes(slip: Slip): Promise<Uint8Array> {
  const info = await shellInfo()
  if (info === undefined) throw new Error('there is no shell to render for')
  // Text mode drops the logo — no decode — and types in the head's own face.
  if (info.receipt.mode === 'text') {
    return renderText(slip, { columns: info.receiptColumns, codepage: info.receiptCodepage })
  }
  await receiptFontReady()
  return rasterBytes(slip, info.receipt.dots, await decodeLogos(slip), FLOW_CONTROLLED.has(info.printerKind))
}

class NoCanvas extends Error {
  constructor() {
    super('this webview has no 2d canvas')
    this.name = 'NoCanvas'
  }
}

/**
 * The links whose printer holds the host off when its buffer is full: USB
 * directly, and the two spoolers, which write to the same driver and wait on it.
 * Leaving `winspool` out sent a Windows kiosk 128-row bands, and a QR is taller
 * than that with no blank row to break on — a white stripe through the code.
 */
const FLOW_CONTROLLED: ReadonlySet<string> = new Set(['usb', 'winspool', 'cups'])

/**
 * `flowControlled` is one of `FLOW_CONTROLLED`. The small default bands exist for serial and raw TCP, which cannot, and
 * each band boundary on the paper is a place the motor stopped — see
 * `toEscPosRaster`.
 */
function rasterBytes(
  slip: Slip,
  dots: number,
  images: ReadonlyMap<string, DecodedImage>,
  flowControlled: boolean
): Uint8Array {
  const raster = drawSlip(slip, { dots, images }, (width, height) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (context === null) throw new NoCanvas()
    return { context, done: () => {} }
  })
  return flowControlled ? toEscPosRaster(raster, BAND_ROWS_FLOW_CONTROLLED) : toEscPosRaster(raster)
}

/**
 * Build and print in one call, which is what every caller actually wants.
 *
 * The template is read from what is in hand rather than passed in, because the
 * one caller on the sale path — `screens/success.tsx` — has a customer standing
 * in front of it and nothing useful to say about which layout to use. It is
 * whatever the shop last fetched, or the built-in one; either is a receipt.
 */
export async function printReceipt(
  order: SlipOrder,
  branch: SlipBranch,
  template: ReceiptTemplate = templateInHand()
): Promise<PrintOutcome> {
  return printSlip(buildSlip(order, branch, template))
}
